import React, {createContext, useCallback, useContext, useMemo, useState} from 'react'
import {safeLocalStorage} from '@/lib/storage'

// ---------------------------------------------------------------------------
// HelpStateContext — global, stable, mounted once at app root.
// Consumers re-render only when helpEnabled toggles.
// ---------------------------------------------------------------------------

interface HelpStateContextValue {
    helpEnabled: boolean
    toggleHelp: () => void
}

const STORAGE_KEY = 'iam-help-enabled'

function readStorageValue(): boolean {
    return safeLocalStorage.get(STORAGE_KEY) === 'true'
}

function writeStorageValue(value: boolean): void {
    safeLocalStorage.set(STORAGE_KEY, value ? 'true' : 'false')
}

const HelpStateContext = createContext<HelpStateContextValue>({
    helpEnabled: false,
    toggleHelp: () => undefined,
})

interface HelpProviderProps {
    children: React.ReactNode
}

export function HelpProvider({children}: HelpProviderProps) {
    const [helpEnabled, setHelpEnabled] = useState<boolean>(() => readStorageValue())

    const toggleHelp = useCallback(() => {
        setHelpEnabled(prev => {
            const next = !prev
            writeStorageValue(next)
            return next
        })
    }, [])

    const stateValue = useMemo<HelpStateContextValue>(
        () => ({helpEnabled, toggleHelp}),
        [helpEnabled, toggleHelp],
    )

    return (
        <HelpStateContext.Provider value={stateValue}>
            {children}
        </HelpStateContext.Provider>
    )
}

/** Access the global help enabled/toggle state. */
export function useHelp(): HelpStateContextValue {
    return useContext(HelpStateContext)
}
